import type { ReactNode } from 'react';
import {
  AlertDialog,
  Autocomplete,
  Button,
  Card,
  Chip,
  Label,
  ListBox,
  SearchField,
  Switch,
  Tooltip,
} from '@heroui/react';
import { ExternalLink as ExternalLinkIcon, Info as InfoIcon } from 'lucide-react';
import { makeMatcher } from '../lib/search';
import { LIGHT_LABEL, formatValue, type Light, type RuleDef, type Segment } from '@odd/domain';

const LIGHT_COLOR = { rojo: 'danger', ambar: 'warning', verde: 'success' } as const;
const DOT = { rojo: 'bg-bad', ambar: 'bg-warn', verde: 'bg-ok' } as const;

/** Semáforo siempre con palabra (principio 4 del brief). */
export function LightChip({ light, size = 'sm' }: { light: Light; size?: 'sm' | 'md' }) {
  return (
    <Chip color={LIGHT_COLOR[light]} variant="soft" size={size} className="shrink-0">
      <span className={`size-2 rounded-full ${DOT[light]}`} aria-hidden />
      <Chip.Label>{LIGHT_LABEL[light]}</Chip.Label>
    </Chip>
  );
}

export function SegmentBadge({ segment }: { segment: Segment | null }) {
  const top = segment === 'A' || segment === 'B';
  return (
    <span
      title={segment ? `Segmento ${segment}` : 'Sin segmento'}
      className={`inline-grid size-6 shrink-0 place-items-center rounded-md text-xs font-extrabold ${
        top ? 'bg-ink text-paper' : 'bg-band text-ink-2'
      }`}
    >
      {segment ?? '·'}
    </span>
  );
}

/** Barra del valor frente a los umbrales ámbar y rojo. */
export function Meter({
  value,
  rule,
  light,
}: {
  value: number;
  rule: Pick<RuleDef, 'amber' | 'red' | 'inv'>;
  light: Light;
}) {
  const max = rule.inv ? rule.amber * 2 : rule.red * 1.6;
  // Acotado a 0–100 % (p. ej. tesorería negativa en una regla inversa no debe salirse de la barra)
  const clamp = (n: number) => Math.min(100, Math.max(0, n));
  const pos = (v: number) =>
    clamp(rule.inv ? 100 - (Math.min(v, max) / max) * 100 : (Math.min(v, max) / max) * 100);
  const fill = light === 'rojo' ? 'bg-bad' : light === 'ambar' ? 'bg-warn' : 'bg-ok';
  return (
    <div className="relative h-1.5 w-full rounded-full bg-band" aria-hidden>
      <div
        className={`absolute inset-y-0 left-0 rounded-full ${fill}`}
        style={{ width: `${Math.max(4, pos(value))}%` }}
      />
      <span className="absolute -inset-y-0.5 w-px bg-ink-3" style={{ left: `${pos(rule.amber)}%` }} />
      <span className="absolute -inset-y-0.5 w-px bg-ink-3" style={{ left: `${pos(rule.red)}%` }} />
    </div>
  );
}

export const valueText = (rule: RuleDef | undefined, v: number) => (rule ? formatValue(rule, v) : String(v));

export function Panel({
  id,
  title,
  description,
  action,
  children,
  className = '',
}: {
  id?: string;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Card id={id} className={`board-panel gap-0 p-0 ${className}`}>
      <Card.Header className="board-panel-header flex flex-row items-start justify-between gap-3 px-5 pt-5 pb-3">
        <div className="min-w-0">
          <Card.Title className="board-panel-title text-base font-extrabold text-ink">{title}</Card.Title>
          {description && (
            <Card.Description className="board-panel-description mt-0.5 text-sm text-ink-3">
              {description}
            </Card.Description>
          )}
        </div>
        {action}
      </Card.Header>
      <Card.Content className="board-panel-content px-5 pb-5">{children}</Card.Content>
    </Card>
  );
}

export function Initials({ name, tone = 'default' }: { name: string; tone?: 'default' | 'bad' }) {
  const ini = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');
  return (
    <span
      className={`inline-grid size-9 shrink-0 place-items-center rounded-full text-xs font-bold ${
        tone === 'bad' ? 'bg-bad-soft text-bad' : 'bg-orange-soft text-orange-ink'
      }`}
      aria-hidden
    >
      {ini || '·'}
    </span>
  );
}

export type SelectOption = {
  value: string;
  label: string;
  /** Línea secundaria bajo la etiqueta (p. ej. AFF o segmento) */
  description?: string;
  /** Texto extra por el que también se puede buscar (no se muestra) */
  keywords?: string;
};

/**
 * Select de HeroUI con buscador incluido (`Autocomplete`): todos los desplegables del panel usan este.
 * La búsqueda es la del resto del panel: sin tildes y por palabras (`lib/search.ts`).
 */
export function Select({
  value,
  onChange,
  options,
  label,
  className = '',
  placeholder = 'Elegir…',
  searchPlaceholder = 'Buscar…',
  emptyText = 'Sin resultados',
  isDisabled,
  isClearable,
}: {
  value: string;
  onChange: (v: string) => void;
  options: SelectOption[];
  label: string;
  className?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  isDisabled?: boolean;
  /** Muestra una «×» para dejar el campo vacío */
  isClearable?: boolean;
}) {
  return (
    <Autocomplete
      aria-label={label}
      className={className}
      placeholder={placeholder}
      isDisabled={isDisabled}
      value={value || null}
      onChange={(k) => onChange(k == null ? '' : String(k))}
      onClear={isClearable ? () => onChange('') : undefined}
    >
      <Autocomplete.Trigger>
        {/* En el campo solo la etiqueta (la descripción se ve en la lista): todos los selects miden igual */}
        <Autocomplete.Value className="min-w-0 flex-1 truncate">
          {({ isPlaceholder, defaultChildren }) =>
            isPlaceholder
              ? defaultChildren
              : (options.find((o) => o.value === value)?.label ?? defaultChildren)
          }
        </Autocomplete.Value>
        {isClearable && value && <Autocomplete.ClearButton />}
        <Autocomplete.Indicator />
      </Autocomplete.Trigger>
      <Autocomplete.Popover>
        <Autocomplete.Filter filter={(text, input) => makeMatcher(input)(text)}>
          <SearchField autoFocus aria-label={`Buscar ${label.toLowerCase()}`} variant="secondary">
            <SearchField.Group>
              <SearchField.SearchIcon />
              <SearchField.Input placeholder={searchPlaceholder} />
              <SearchField.ClearButton />
            </SearchField.Group>
          </SearchField>
          <ListBox
            className="max-h-72 overflow-y-auto"
            renderEmptyState={() => <p className="px-3 py-4 text-center text-sm text-ink-3">{emptyText}</p>}
          >
            {options.map((o) => (
              <ListBox.Item
                key={o.value}
                id={o.value}
                textValue={[o.label, o.description, o.keywords].filter(Boolean).join(' ')}
              >
                <div className="grid min-w-0">
                  <span className="truncate">{o.label}</span>
                  {o.description && <span className="truncate text-xs text-ink-3">{o.description}</span>}
                </div>
                <ListBox.ItemIndicator />
              </ListBox.Item>
            ))}
          </ListBox>
        </Autocomplete.Filter>
      </Autocomplete.Popover>
    </Autocomplete>
  );
}

/**
 * Interruptor de HeroUI v3. Ojo: `Switch.Content` es el botón clicable y debe envolver el control y la
 * etiqueta; si el control queda fuera, se pinta pero no responde al clic.
 */
export function Toggle({
  isSelected,
  onChange,
  label,
  isDisabled,
  size = 'sm',
  ariaLabel,
}: {
  isSelected: boolean;
  onChange: (v: boolean) => void;
  label: ReactNode;
  isDisabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  ariaLabel?: string;
}) {
  return (
    <Switch
      size={size}
      isSelected={isSelected}
      isDisabled={isDisabled}
      onChange={onChange}
      aria-label={ariaLabel}
    >
      <Switch.Content>
        <Switch.Control>
          <Switch.Thumb />
        </Switch.Control>
        <Label className="cursor-pointer text-xs text-ink-2">{label}</Label>
      </Switch.Content>
    </Switch>
  );
}

/** Número compacto con icono (estilo clave–valor); el significado va en el tooltip y en aria-label. */
export function CountChip({
  icon: Icon,
  value,
  label,
  tone = 'default',
}: {
  icon: React.ComponentType<{ className?: string }>;
  value: number;
  label: string;
  tone?: 'default' | 'accent' | 'bad' | 'ok';
}) {
  const tones = {
    default: 'bg-card-2 text-ink-2',
    accent: 'bg-orange-soft text-orange-ink',
    bad: 'bg-bad-soft text-bad',
    ok: 'bg-ok-soft text-ok',
  };
  return (
    <span
      title={`${label}: ${value}`}
      aria-label={`${label}: ${value}`}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold ${tones[tone]}`}
    >
      <Icon className="size-3.5" />
      <span className="tabular">{value}</span>
    </span>
  );
}

/** Enlace externo (nueva pestaña) a la web del ERP. No anidar dentro de otro botón o enlace. */
export function ErpLink({
  href,
  children,
  className = '',
  title = 'Abrir en Eleva',
}: {
  href: string;
  children?: ReactNode;
  className?: string;
  title?: string;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={title}
      aria-label={typeof children === 'string' ? `${children} (abre Eleva)` : title}
      onClick={(e) => e.stopPropagation()}
      className={`inline-flex items-center gap-1 text-orange-ink hover:underline ${className}`}
    >
      {children}
      <ExternalLinkIcon className="size-3.5 shrink-0" />
    </a>
  );
}

/** Confirmación con el AlertDialog de HeroUI (sustituye a window.confirm) */
export function ConfirmDialog({
  isOpen,
  onClose,
  title,
  children,
  confirmLabel = 'Eliminar',
  onConfirm,
  isPending = false,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children?: ReactNode;
  confirmLabel?: string;
  onConfirm: () => void;
  isPending?: boolean;
}) {
  return (
    <AlertDialog.Backdrop isOpen={isOpen} onOpenChange={(o) => !o && onClose()} isDismissable>
      <AlertDialog.Container size="sm">
        <AlertDialog.Dialog>
          <AlertDialog.Header>
            <AlertDialog.Icon status="danger" />
            <AlertDialog.Heading>{title}</AlertDialog.Heading>
          </AlertDialog.Header>
          {children && <AlertDialog.Body className="text-sm text-ink-2">{children}</AlertDialog.Body>}
          <AlertDialog.Footer>
            <Button variant="ghost" onPress={onClose}>
              Cancelar
            </Button>
            <Button variant="danger" isPending={isPending} onPress={onConfirm}>
              {confirmLabel}
            </Button>
          </AlertDialog.Footer>
        </AlertDialog.Dialog>
      </AlertDialog.Container>
    </AlertDialog.Backdrop>
  );
}

/** Icono «i» con una explicación breve en tooltip (ratón o teclado: tab + foco) */
export function InfoTip({ children, label = 'Qué significa' }: { children: ReactNode; label?: string }) {
  return (
    <Tooltip delay={150} closeDelay={100}>
      {/* El Trigger ya es el elemento enfocable (role=button): no anidar otro botón dentro */}
      <Tooltip.Trigger
        aria-label={label}
        className="inline-grid size-4 cursor-help place-items-center rounded-full text-ink-3 outline-none hover:text-ink focus-visible:text-ink focus-visible:ring-2 focus-visible:ring-orange"
      >
        <InfoIcon className="size-3.5" />
      </Tooltip.Trigger>
      <Tooltip.Content
        showArrow
        className="max-w-72 text-xs leading-relaxed font-normal normal-case tracking-normal break-normal [overflow-wrap:normal] [word-break:normal]"
      >
        {children}
      </Tooltip.Content>
    </Tooltip>
  );
}
